import { memo, useMemo } from "react";
import styled from "styled-components";
import {
  CheckIcon,
  InformationCircleIcon,
  Square2StackIcon,
} from "@heroicons/react/24/outline";
import { External, Spinner } from "@sablier/v2-components/atoms";
import { Button, Label } from "@sablier/v2-components/molecules";
import { links } from "@sablier/v2-constants";
import { useCopy, useShortAddress } from "@sablier/v2-hooks";
import { Trans, useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Warning } from "~/client/components/molecules";
import { useAccount } from "~/client/hooks";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
  }
`;

const Section = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
    *[data-component="button"] {
      justify-content: flex-start;
    }
  }
`;

const Description = styled.div`
  & > p {
    ${(props) => props.theme.styles.textParagraph}
    & {
      color: ${(props) => props.theme.colors.gray200};
      line-height: 20pt;
    }
  }
`;

const Info = memo(function Info() {
  return (
    <div>
      <Trans
        i18nKey={"structs.readDocsAt"}
        components={[<External key={"0"} to={links.docs.proxy}></External>]}
      />
    </div>
  );
});

const Loader = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    justify-content: center;
    width: 100%;
    height: 300px;
  }
`;

const WarningProxy = styled(Warning.Proxy)`
  border: 2px solid ${(props) => props.theme.colors.dark400};
  background-color: ${(props) => props.theme.colors.dark000};
`;

const Wrapper = styled(WrapperPartial)``;

function Configure() {
  const { t } = useT();
  const { isConfigured, proxy } = useAccount();

  const isProxyAvailable = useMemo(() => _.isEthereumAddress(proxy), [proxy]);
  const shortProxy = useShortAddress(proxy, 10, -8);
  const [isCopied, doCopy] = useCopy(proxy || "");

  const isLoading = useMemo(() => !isConfigured, [isConfigured]);

  return (
    <Wrapper>
      <Content>
        <Section>
          <Label
            value={t("modalCards.proxy.instance.title")}
            icon={InformationCircleIcon}
            isIconLast
            tooltip={{
              value: <Info />,
              trigger: "hover",
              color: "dark000",
              arrow: true,
            }}
          />
          <Description>
            <p>{t("modalCards.proxy.instance.description")}</p>
          </Description>
        </Section>
        {isLoading ? (
          <Loader>
            <Spinner color={"white"} />
          </Loader>
        ) : (
          <>
            <Section>
              {isProxyAvailable ? (
                <Button
                  accent={"dark200"}
                  appearance={"solid"}
                  left={proxy}
                  leftPurpose={"identicon"}
                  right={isCopied ? CheckIcon : Square2StackIcon}
                  onClick={doCopy}
                  purpose={"button"}
                  title={`${proxy}${isCopied ? ` (${t("words.copied")})` : ""}`}
                  titleMedium={shortProxy}
                />
              ) : (
                <WarningProxy />
              )}
            </Section>
          </>
        )}
      </Content>
    </Wrapper>
  );
}

export default Configure;
